import React, { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { PrinterIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { Button, LoadingState } from './ui';
import NeaLogo from './NeaLogo';
import { formatFecha, tieneCoordenada } from '../utils/fecha';
import { cargarImagenComoDataUri } from '../utils/imagenes';

// Interfaces reutilizadas de CargarEnsayos
interface EnsayoData {
  id: number;
  analito: string;
  analito_id: string;
  resultado: string;
  unidad: string;
  metodo: string;
  limite_deteccion: string;
}

interface MuestraData {
  id: number;
  id_nea: string;
  id_m_origen: string;
  id_m_lab: string;
  id_muestra_lab: string;
  responsable: string;
  pertenencia: string;
  sitio_muestreo: string;
  georr_lat: string;
  georr_long: string;
  medio: string;
  fecha_muestreo: string;
  ensayos: EnsayoData[];
  mostrar_organismo_control: boolean;
  organismo_control_id: number | null;
  organismo_control: string;
  mostrar_cadena_custodia: boolean;
  cadena_custodia: string;
  remito?: string;
  descripcion?: string;
}

interface AnalisisData {
  id: number;
  id_cliente: number;
  id_laboratorio: number;
  id_origen: number;
  /** Número NEA del análisis, el identificador que se comunica al cliente. */
  numero_nea: string;
  /** 1 = finalizado. Un análisis pendiente todavía no tiene resultados. */
  estado: number;
  fecha_muestreo: string;
  fecha_recepcion: string;
  fecha_analisis: string;
  fecha_emision: string;
  organismo_control: boolean;
  cadena_custodia: boolean;
  muestras: MuestraData[];
  // Campos del solicitante
  solicitante: string;
  cuit_sol: string;
  direccion_sol: string;
  // Campos del laboratorio
  laboratorio: string;
  cuit_lab: string;
  direccion_lab: string;
  // Campos adicionales
  planta: string;
  mostrar_numero_origen: boolean;
  numero_analisis_origen: string;
  mostrar_numero_laboratorio: boolean;
  numero_analisis_laboratorio: string;
  analistas?: string;
  firma?: string;
  director?: string;
  logo?: string;
}

interface AnalisisPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  analisisId: number;
}

const celdaCabecera: React.CSSProperties = {
  border: '1px solid black',
  padding: '5px',
  textAlign: 'left',
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
};

// Componente separado para el contenido del PDF
interface PdfContentProps {
  analisis: AnalisisData;
  analisisId: number;
  /** Firma y logo ya incrustados como data URI, o null si no están disponibles. */
  firmaSrc: string | null;
  logoSrc: string | null;
  cargandoImagenes: boolean;
}

const PdfContent = React.forwardRef<HTMLDivElement, PdfContentProps>((props, ref) => {
  const { analisis, analisisId, firmaSrc, logoSrc, cargandoImagenes } = props;
  // El informe lleva firma o logo si el laboratorio los eligió al cerrarlo.
  const esperaLogo = !!analisis.logo && analisis.logo !== 'no especifica.png';
  const esperaFirma = !!analisis.firma && analisis.firma !== 'no especifica.png';
  // Mientras el análisis está pendiente la vista previa muestra qué se va a
  // ensayar, no resultados: todavía no hay valores cargados.
  const finalizado = analisis.estado === 1;

  return (
    <div ref={ref} style={{
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      lineHeight: '1.4',
      color: 'black',
      backgroundColor: 'white',
      width: '210mm',
      minHeight: '297mm'
    }}>
      {/* Cabecera: logo de NEA a la izquierda, número de análisis a la derecha */}
      <div style={{
        borderBottom: '2px solid black',
        paddingBottom: '15px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <NeaLogo height={46} />

          <h1 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0'
          }}>
            INFORME DE ANÁLISIS
          </h1>

          {/* Logo propio del laboratorio, si el informe lo lleva */}
          {esperaLogo && logoSrc && (
            <img
              src={logoSrc}
              alt="Logo del laboratorio"
              style={{
                width: '80px',
                height: '50px',
                objectFit: 'contain'
              }}
            />
          )}
          {esperaLogo && !logoSrc && !cargandoImagenes && (
            <span style={{ fontSize: '9px', color: '#92400e' }}>Logo no disponible</span>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>Análisis N°</p>
          <p style={{ margin: '0', fontSize: '16px', fontWeight: 'bold' }}>
            {analisis.numero_nea || analisisId}
          </p>
          {!finalizado && (
            <p style={{ margin: '4px 0 0', fontSize: '11px', fontWeight: 'bold', color: '#92400e' }}>
              PENDIENTE
            </p>
          )}
        </div>
      </div>

      {/* Información del Solicitante y Laboratorio */}
      <div style={{
        borderBottom: '1px solid black',
        paddingBottom: '15px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          {/* Sección del Solicitante */}
          <div style={{ borderRight: '1px solid black', paddingRight: '20px' }}>
            <h2 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
              SOLICITANTE
            </h2>
            <div>
              <div style={{ marginBottom: '5px' }}>
                <strong>Razón Social: </strong>
                <span>{analisis.solicitante}</span>
              </div>
              {analisis.cuit_sol && (
                <div style={{ marginBottom: '5px' }}>
                  <strong>CUIT: </strong>
                  <span>{analisis.cuit_sol}</span>
                </div>
              )}
              {analisis.direccion_sol && (
                <div style={{ marginBottom: '5px' }}>
                  <strong>Dirección: </strong>
                  <span>{analisis.direccion_sol}</span>
                </div>
              )}
              {analisis.planta && (
                <div style={{ marginBottom: '5px' }}>
                  <strong>Planta: </strong>
                  <span>{analisis.planta}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sección del Laboratorio */}
          <div>
            <h2 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
              LABORATORIO
            </h2>
            <div>
              <div style={{ marginBottom: '5px' }}>
                <strong>Razón Social: </strong>
                <span>{analisis.laboratorio}</span>
              </div>
              {analisis.cuit_lab && (
                <div style={{ marginBottom: '5px' }}>
                  <strong>CUIT: </strong>
                  <span>{analisis.cuit_lab}</span>
                </div>
              )}
              {analisis.direccion_lab && (
                <div style={{ marginBottom: '5px' }}>
                  <strong>Dirección: </strong>
                  <span>{analisis.direccion_lab}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fechas */}
      <div style={{
        borderBottom: '1px solid black',
        paddingBottom: '15px',
        marginBottom: '20px'
      }}>
        <h2 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
          FECHAS
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          <div>
            <strong>Fecha de análisis: </strong>
            <span>{analisis.fecha_analisis}</span>
          </div>
          {analisis.fecha_muestreo && (
            <div>
              <strong>Fecha de muestreo: </strong>
              <span>{analisis.fecha_muestreo}</span>
            </div>
          )}
        </div>
      </div>

              {/* Muestras y Ensayos */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '15px' }}>
            MUESTRAS Y ENSAYOS
          </h2>

                    {analisis.muestras && analisis.muestras.length > 0 ? analisis.muestras.map((muestra, muestraIndex) => (
            <div key={muestraIndex} style={{
              border: '1px solid black',
              padding: '15px',
              marginBottom: '20px',
              pageBreakInside: 'avoid'
            }}>
              <h3 style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
                MUESTRA {muestraIndex + 1}
              </h3>

              {/* Información de la muestra */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '10px',
                marginBottom: '15px',
                fontSize: '10px'
              }}>
                {muestra.id_m_origen && (
                  <div>
                    <strong>ID Origen: </strong>
                    <span>{muestra.id_m_origen}</span>
                  </div>
                )}
                {muestra.id_m_lab && (
                  <div>
                    <strong>ID Lab: </strong>
                    <span>{muestra.id_m_lab}</span>
                  </div>
                )}
                {muestra.responsable && (
                  <div>
                    <strong>Nombre del responsable: </strong>
                    <span>{muestra.responsable}</span>
                  </div>
                )}
                {muestra.sitio_muestreo && (
                  <div>
                    <strong>Sitio: </strong>
                    <span>{muestra.sitio_muestreo}</span>
                  </div>
                )}
                {muestra.fecha_muestreo && (
                  <div>
                    <strong>Fecha muestreo: </strong>
                    <span>{muestra.fecha_muestreo}</span>
                  </div>
                )}
                {muestra.medio && (
                  <div>
                    <strong>Medio: </strong>
                    <span>{muestra.medio}</span>
                  </div>
                )}
                {/* Una coordenada en cero es una coordenada sin cargar. */}
                {tieneCoordenada(muestra.georr_lat) && (
                  <div>
                    <strong>Latitud: </strong>
                    <span>{muestra.georr_lat}</span>
                  </div>
                )}
                {tieneCoordenada(muestra.georr_long) && (
                  <div>
                    <strong>Longitud: </strong>
                    <span>{muestra.georr_long}</span>
                  </div>
                )}
                {muestra.mostrar_organismo_control && muestra.organismo_control && (
                  <div>
                    <strong>Organismo control: </strong>
                    <span>{muestra.organismo_control}</span>
                  </div>
                )}
                {muestra.mostrar_cadena_custodia && muestra.cadena_custodia && (
                  <div>
                    <strong>Cadena custodia: </strong>
                    <span>{muestra.cadena_custodia}</span>
                  </div>
                )}
                {muestra.remito && (
                  <div>
                    <strong>Remito: </strong>
                    <span>{muestra.remito}</span>
                  </div>
                )}
              </div>

              {/* Tabla de ensayos */}
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '9px'
              }}>
                <thead>
                  <tr>
                    <th style={celdaCabecera}>ANALITO</th>
                    {finalizado && (
                      <>
                        <th style={celdaCabecera}>RESULTADO</th>
                        <th style={celdaCabecera}>UNIDAD</th>
                        <th style={celdaCabecera}>MÉTODO</th>
                        <th style={celdaCabecera}>L.D.</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {muestra.ensayos && muestra.ensayos.length > 0 ? muestra.ensayos.map((ensayo, ensayoIndex) => (
                    <tr key={ensayoIndex}>
                      <td style={{ border: '1px solid black', padding: '5px' }}>
                        {ensayo.analito}
                      </td>
                      {finalizado && (
                        <>
                          <td style={{ border: '1px solid black', padding: '5px', fontWeight: 'bold' }}>
                            {ensayo.resultado || '-'}
                          </td>
                          <td style={{ border: '1px solid black', padding: '5px' }}>
                            {ensayo.unidad}
                          </td>
                          <td style={{ border: '1px solid black', padding: '5px' }}>
                            {ensayo.metodo || '-'}
                          </td>
                          <td style={{ border: '1px solid black', padding: '5px' }}>
                            {ensayo.limite_deteccion || '-'}
                          </td>
                        </>
                      )}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={finalizado ? 5 : 1} style={{ border: '1px solid black', padding: '10px', textAlign: 'center' }}>
                        No hay ensayos disponibles para esta muestra
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Observaciones de la muestra */}
              {muestra.descripcion && (
                <div style={{ marginTop: '10px' }}>
                  <h4 style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>
                    OBSERVACIONES:
                  </h4>
                  <p style={{ fontSize: '10px', margin: '0' }}>
                    {muestra.descripcion}
                  </p>
                </div>
              )}
            </div>
          )) : (
            <div style={{
              border: '1px solid black',
              padding: '20px',
              textAlign: 'center',
              fontSize: '12px'
            }}>
              No hay muestras disponibles para este análisis
            </div>
          )}
      </div>

      {/* Firmas y Analistas */}
      <div style={{
        borderTop: '1px solid black',
        paddingTop: '15px',
        pageBreakInside: 'avoid'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          {/* Analistas */}
          <div>
            <h3 style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '10px' }}>
              ANALISTAS
            </h3>
            {analisis.analistas ? (
              <div>
                {analisis.analistas.split(';').map((analista, index) => (
                  <p key={index} style={{ fontSize: '10px', margin: '2px 0' }}>
                    • {analista.trim()}
                  </p>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '10px' }}>Sin analistas especificados</p>
            )}

            {analisis.director && (
              <div style={{ marginTop: '14px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
                  DIRECTOR TÉCNICO:
                </h4>
                <p style={{ fontSize: '11px', margin: '0' }}>{analisis.director}</p>
              </div>
            )}
          </div>

          {/* Firma */}
          <div>
            <h3 style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
              FIRMA
            </h3>
            <div style={{
              border: '1px solid black',
              width: '160px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {esperaFirma && firmaSrc && (
                <img
                  src={firmaSrc}
                  alt="Firma"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
              {esperaFirma && !firmaSrc && (
                <span style={{ fontSize: '10px', color: cargandoImagenes ? '#666' : '#92400e' }}>
                  {cargandoImagenes ? 'Cargando firma...' : 'Firma no disponible'}
                </span>
              )}
              {!esperaFirma && <span style={{ fontSize: '10px' }}>Sin firma</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PdfContent.displayName = 'PdfContent';

const AnalisisPdfModal: React.FC<AnalisisPdfModalProps> = ({ isOpen, onClose, analisisId }) => {
  const [analisis, setAnalisis] = useState<AnalisisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [firmaSrc, setFirmaSrc] = useState<string | null>(null);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [cargandoImagenes, setCargandoImagenes] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const cargarAnalisis = async () => {
    if (!analisisId) return;

    setLoading(true);
    try {
      const response = await api.get(`/analisis/${analisisId}`);

      // Cargar información adicional de medios, organismos de control, etc.
      const mediosMap = new Map();
      const organismosMap = new Map();

      try {
        // Cargar medios para mapear IDs a nombres
        const mediosResponse = await api.get('/ensayos-medio');
        if (mediosResponse.data) {
          mediosResponse.data.forEach((medio: any) => {
            mediosMap.set(medio.id.toString(), medio.medio);
          });
        }
      } catch (err) {
        console.error('Error al cargar medios:', err);
      }

      try {
        // Cargar organismos de control para mapear IDs a nombres
        const organismosResponse = await api.get('/organismos-control');
        if (organismosResponse.data) {
          organismosResponse.data.forEach((org: any) => {
            organismosMap.set(org.id.toString(), org.organismo);
          });
        }
      } catch (err) {
        console.error('Error al cargar organismos de control:', err);
      }

      // Transformar los datos para que coincidan con la interfaz esperada
      const rawData = response.data;
      const transformedData: AnalisisData = {
        id: rawData.id || 0,
        id_cliente: rawData.id_origen || 0,
        id_laboratorio: rawData.id_lab || 0,
        id_origen: rawData.id_origen || 0,
        numero_nea: rawData.id_a_nea || '',
        estado: Number(rawData.estado ?? 0),
        fecha_muestreo: formatFecha(rawData.fecha_muestreo),
        fecha_recepcion: formatFecha(rawData.fecha_recepcion),
        fecha_analisis: formatFecha(rawData.fecha_solicitud),
        fecha_emision: formatFecha(rawData.fecha_emision),
        organismo_control: !!rawData.organismo_control,
        cadena_custodia: !!rawData.cadena_custodia,
        muestras: (rawData.muestra || []).map((m: any) => ({
          id: m.id || 0,
          id_nea: m.id_m_nea || '',
          id_m_origen: m.id_m_origen || '',
          id_m_lab: m.id_m_lab || '',
          id_muestra_lab: m.id_muestra_lab || '',
          responsable: m.responsable || '',
          pertenencia: m.pertenencia || '',
          sitio_muestreo: m.sitio_muestreo?.denominacion || '',
          georr_lat: m.sitio_muestreo?.georreferencia_lat?.toString() || '',
          georr_long: m.sitio_muestreo?.georreferencia_long?.toString() || '',
          medio: mediosMap.get(m.medio?.toString()) || m.medio || '',
          fecha_muestreo: formatFecha(m.fecha_muestreo),
          ensayos: (m.ensayo || []).map((e: any) => ({
            id: e.id || 0,
            analito_id: e.id_ensayo?.toString() || '',
            // El backend adjunta el nombre del analito desde el catálogo; si
            // falta se muestra el número para no dejar la celda vacía.
            analito: e.analito || `Ensayo ${e.id_ensayo ?? ''}`.trim(),
            resultado: e.valor?.toString() || '',
            unidad: e.unidad || e.unidad_catalogo || '',
            metodo: e.metodo_ensayo || '',
            limite_deteccion: e.limite_deteccion?.toString() || ''
          })),
          mostrar_organismo_control: !!m.organismo,
          organismo_control_id: m.organismo || null,
          organismo_control: organismosMap.get(m.organismo?.toString()) || '',
          mostrar_cadena_custodia: !!m.custodia,
          cadena_custodia: m.custodia || '',
          remito: m.remito || '',
          descripcion: m.observaciones || ''
        })),
        solicitante: rawData.cliente?.nombre || rawData.cliente?.razon_social || rawData.solicitante || 'No especificado',
        cuit_sol: rawData.cliente?.cuit || rawData.cuit_sol || '',
        direccion_sol: rawData.cliente?.direccion ?
          `${rawData.cliente.direccion}${rawData.cliente.ciudad ? `, ${rawData.cliente.ciudad}` : ''}${rawData.cliente.provincia ? `, ${rawData.cliente.provincia}` : ''}` :
          rawData.direccion_sol || '',
        laboratorio: rawData.laboratorio?.nombre || rawData.laboratorio?.razon_social || rawData.laboratorio || 'No especificado',
        cuit_lab: rawData.laboratorio?.cuit || rawData.cuit_lab || '',
        direccion_lab: rawData.laboratorio?.direccion ?
          `${rawData.laboratorio.direccion}${rawData.laboratorio.ciudad ? `, ${rawData.laboratorio.ciudad}` : ''}${rawData.laboratorio.provincia ? `, ${rawData.laboratorio.provincia}` : ''}` :
          rawData.direccion_lab || '',
        planta: rawData.planta || '',
        mostrar_numero_origen: !!rawData.id_a_cliente,
        numero_analisis_origen: rawData.id_a_cliente || rawData.numero_analisis_origen || '',
        mostrar_numero_laboratorio: !!rawData.id_a_lab,
        numero_analisis_laboratorio: rawData.id_a_lab || rawData.numero_analisis_laboratorio || '',
        analistas: rawData.analistas || '',
        firma: rawData.firma || '',
        director: rawData.director || '',
        logo: rawData.logo || ''
      };

      setAnalisis(transformedData);

      // La firma y el logo se incrustan en el documento: así se ven en la vista
      // previa y también en el PDF impreso.
      setCargandoImagenes(true);
      const [firma, logo] = await Promise.all([
        cargarImagenComoDataUri(transformedData.firma),
        cargarImagenComoDataUri(transformedData.logo),
      ]);
      setFirmaSrc(firma);
      setLogoSrc(logo);
      setCargandoImagenes(false);
    } catch (error) {
      console.error('Error:', error);
      setCargandoImagenes(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && analisisId) {
      setFirmaSrc(null);
      setLogoSrc(null);
      cargarAnalisis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, analisisId]);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Análisis_${analisis?.numero_nea || analisisId}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 15mm 20mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
        }
      }
    `
  });

  if (!isOpen) return null;

  // Sólo se imprime un informe cerrado: un análisis pendiente no tiene resultados.
  const finalizado = analisis?.estado === 1;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Header del modal */}
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-slate-900">
              Vista previa del PDF - Análisis {analisis?.numero_nea || analisisId}
            </h2>
            {finalizado ? (
              <Button onClick={handlePrint} icon={<PrinterIcon className="h-4 w-4" />}>
                Generar PDF
              </Button>
            ) : (
              analisis && (
                <span className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  El análisis está pendiente: sólo se imprimen los análisis finalizados.
                </span>
              )
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            title="Cerrar"
            className="text-slate-400 hover:text-slate-600 rounded-md p-1 hover:bg-slate-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Contenido scrolleable */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <LoadingState label="Generando vista previa..." />
          ) : analisis ? (
            <div className="p-4 bg-gray-50">
              <PdfContent
                ref={componentRef}
                analisis={analisis}
                analisisId={analisisId}
                firmaSrc={firmaSrc}
                logoSrc={logoSrc}
                cargandoImagenes={cargandoImagenes}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AnalisisPdfModal;
